"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import type { Signal } from "@/lib/types";
import type { SignalDeliveryRow } from "@/lib/types/routing";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { statusChip } from "@/lib/status-tokens";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function OverallStatusBadge({ status }: { status: string | null }) {
  const colorMap: Record<string, string> = {
    Received: statusChip("info"),
    Rejected: statusChip("destructive"),
    RoutedAll: statusChip("profit"),
    RoutedPartial: statusChip("warning"),
    RoutedNone: statusChip("destructive"),
  };
  const display = status ?? "-";
  const cls =
    colorMap[display] ?? "bg-muted text-muted-foreground border-border";
  return (
    <span
      className={`inline-flex items-center border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`}
    >
      {display}
    </span>
  );
}

function DeliveryStatusBadge({ status, deliveryStatusLabels }: { status: string; deliveryStatusLabels: Record<string, string> }) {
  const colorMap: Record<string, string> = {
    Pending: statusChip("neutral"),
    Filtered: statusChip("warning"),
    Delivered: statusChip("profit"),
    Executed: statusChip("profit"),
    Failed: statusChip("destructive"),
    NotDelivered: statusChip("destructive"),
  };
  const cls = colorMap[status] ?? "bg-muted text-muted-foreground";
  const label = deliveryStatusLabels[status] ?? status;
  return (
    <Badge variant="outline" className={cls}>
      {label}
    </Badge>
  );
}

function TargetKindBadge({ kind }: { kind: string }) {
  const labels: Record<string, string> = {
    Mt5: "MT5",
    TelegramChannel: "Telegram",
    DiscordWebhook: "Discord",
    HttpWebhook: "HTTP",
  };
  return (
    <span className="inline-flex items-center border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {labels[kind] ?? kind}
    </span>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="text-end text-xs">{value}</span>
    </div>
  );
}

function fmt(ts: string | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleString();
}

interface DeliveryCardLabels {
  filteredByLabel: string;
  latencyLabel: string;
  ticketLabel: string;
  priceLabel: string;
  slippageLabel: string;
  externalIdLabel: string;
  httpStatusLabel: string;
  createdAtLabel: string;
  dispatchedAtLabel: string;
  confirmedAtLabel: string;
  errorLabel: string;
  ignoredParamsLabel: string;
  deliveryStatusLabels: Record<string, string>;
}

function DeliveryCard({ delivery, labels }: { delivery: SignalDeliveryRow; labels: DeliveryCardLabels }) {
  const isMt5 = delivery.targetKind === "Mt5";
  const isTelegram = delivery.targetKind === "TelegramChannel";
  const isDiscord = delivery.targetKind === "DiscordWebhook";
  const isHttp = delivery.targetKind === "HttpWebhook";

  return (
    <div className="border border-[color:var(--line)] bg-[color:var(--bg-1,hsl(var(--card)))] p-4">
      {/* Card header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{delivery.targetName}</span>
          <TargetKindBadge kind={delivery.targetKind} />
        </div>
        <DeliveryStatusBadge status={delivery.status} deliveryStatusLabels={labels.deliveryStatusLabels} />
      </div>

      {/* Divider */}
      <div className="mb-3 border-t border-[color:var(--line-soft,hsl(var(--border)))]" />

      {/* Filtered by */}
      {delivery.filteredByStep && (
        <p className="mb-2 font-mono text-[11px] text-muted-foreground">
          {labels.filteredByLabel}:{" "}
          <span className="text-foreground">{delivery.filteredByStep}</span>
        </p>
      )}

      {/* Latency */}
      <Row
        label={labels.latencyLabel}
        value={delivery.latencyMs != null ? `${delivery.latencyMs}ms` : null}
      />

      {/* MT5-specific */}
      {isMt5 && (
        <>
          <Row
            label={labels.ticketLabel}
            value={
              delivery.executionTicket != null ? (
                <code className="font-mono">#{delivery.executionTicket}</code>
              ) : null
            }
          />
          <Row
            label={labels.priceLabel}
            value={
              delivery.executionPrice != null ? delivery.executionPrice : null
            }
          />
          <Row
            label={labels.slippageLabel}
            value={
              delivery.executionSlippage != null
                ? `${delivery.executionSlippage} pts`
                : null
            }
          />
        </>
      )}

      {/* Telegram/Discord external message id */}
      {(isTelegram || isDiscord) && delivery.externalMessageId && (
        <Row
          label={labels.externalIdLabel}
          value={
            isTelegram && delivery.externalMessageId ? (
              <span className="font-mono text-[11px]">
                {delivery.externalMessageId}
              </span>
            ) : isDiscord && delivery.externalMessageId ? (
              <span className="font-mono text-[11px]">
                {delivery.externalMessageId}
              </span>
            ) : null
          }
        />
      )}

      {/* HTTP webhook status */}
      {isHttp && (
        <Row
          label={labels.httpStatusLabel}
          value={
            delivery.httpStatusCode != null ? (
              <code className="font-mono">{delivery.httpStatusCode}</code>
            ) : null
          }
        />
      )}

      {/* Timestamps */}
      <Row label={labels.createdAtLabel} value={fmt(delivery.createdAt)} />
      <Row label={labels.dispatchedAtLabel} value={fmt(delivery.dispatchedAt)} />
      <Row label={labels.confirmedAtLabel} value={fmt(delivery.confirmedAt)} />

      {/* Params the executing client skipped */}
      {delivery.ignoredParams && delivery.ignoredParams.length > 0 && (
        <div className="mt-3 rounded-lg border border-warning/30 bg-warning/10 p-2">
          <p className="font-mono text-[11px] text-warning">
            {labels.ignoredParamsLabel}: {delivery.ignoredParams.join(", ")}
          </p>
        </div>
      )}

      {/* Error */}
      {delivery.errorMessage && (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-2">
          <p className="font-mono text-[11px] text-destructive">
            {labels.errorLabel}: {delivery.errorMessage}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface DeliveryDrawerProps {
  signal: Signal | null;
  open: boolean;
  onClose: () => void;
}

export function DeliveryDrawer({ signal, open, onClose }: DeliveryDrawerProps) {
  const t = useTranslations("dashboard.signals.drawer");
  const [deliveries, setDeliveries] = useState<SignalDeliveryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const deliveryCardLabels: DeliveryCardLabels = {
    filteredByLabel: t("filteredByLabel"),
    latencyLabel: t("latencyLabel"),
    ticketLabel: t("ticketLabel"),
    priceLabel: t("priceLabel"),
    slippageLabel: t("slippageLabel"),
    externalIdLabel: t("externalIdLabel"),
    httpStatusLabel: t("httpStatusLabel"),
    createdAtLabel: t("createdAtLabel"),
    dispatchedAtLabel: t("dispatchedAtLabel"),
    confirmedAtLabel: t("confirmedAtLabel"),
    errorLabel: t("errorLabel"),
    ignoredParamsLabel: t("ignoredParamsLabel"),
    deliveryStatusLabels: {
      Pending: t("deliveryStatus.Pending"),
      Filtered: t("deliveryStatus.Filtered"),
      Delivered: t("deliveryStatus.Delivered"),
      Executed: t("deliveryStatus.Executed"),
      Failed: t("deliveryStatus.Failed"),
      NotDelivered: t("deliveryStatus.NotDelivered"),
    },
  };

  // Fetch deliveries when opening
  useEffect(() => {
    if (!open || !signal) return;
    let ignore = false;
    const signalId = signal.id;
    async function load() {
      setLoading(true);
      setDeliveries([]);
      try {
        const rows = await api.signals.deliveries(signalId);
        if (!ignore) setDeliveries(rows);
      } catch {
        if (!ignore) setDeliveries([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    void load();
    return () => {
      ignore = true;
    };
  }, [open, signal]);

  // ESC key support
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus trap: focus the drawer when it opens
  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [open]);

  if (!open) return null;

  const overallStatus = signal?.overallStatus ?? signal?.status ?? null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("title")}
        tabIndex={-1}
        className="fixed inset-e-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-s border-[color:var(--line,hsl(var(--border)))] bg-background shadow-2xl outline-none"
        style={{ outline: "none" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-[color:var(--line,hsl(var(--border)))] p-5">
          <div className="space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {t("title")}
            </p>
            {signal && (
              <div className="flex flex-wrap items-center gap-2">
                {signal.symbol && (
                  <span className="font-mono font-medium">{signal.symbol}</span>
                )}
                {signal.action && (
                  <span className="text-sm text-muted-foreground">
                    {signal.action}
                  </span>
                )}
                {signal.volume != null && (
                  <span className="text-sm text-muted-foreground">
                    vol={signal.volume}
                    {signal.volumeType ? ` (${signal.volumeType})` : ""}
                  </span>
                )}
                <OverallStatusBadge status={overallStatus} />
              </div>
            )}
            {signal && (
              <p className="text-xs text-muted-foreground">
                {new Date(signal.createdAt).toLocaleString()}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
            aria-label={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((n) => (
                <div key={n} className="h-32 animate-pulse bg-muted" />
              ))}
              <p className="text-center text-xs text-muted-foreground">
                {t("loading")}
              </p>
            </div>
          ) : deliveries.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("noDeliveries")}
            </p>
          ) : (
            <div className="space-y-3">
              {deliveries.map((d) => (
                <DeliveryCard key={d.id} delivery={d} labels={deliveryCardLabels} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
